import { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { apiFetch } from '../../../../api/crud';
import { BROWSER_EXTENSION_URL } from '../../config/externalURL';
import { SHIPMENTS_TRACKERS_PATH } from '../../config/service';
import Dashboard from '../../global/Dashboard';
import MarketingBannerV2 from '../../global/MarketingBannerV2';
import { DASHBOARD_V2_SHELF_TYPES, REWARDS_V2_SHELF_TYPES } from '../../global/RewardsV2Shelf';
import RewardsStoresShelf from '../../global/RewardsStoresShelf';
import OffersPreviewV2 from '../OffersV2/OffersPreviewV2';
import { getCategories } from '../../utils/service';
import { FIXED_DEALS_CATEGORIES } from '../Rewards/utils';
import LaptopPromosImage from '../../images/laptop-promos.png';
import CategoryTabs from '../../global/Mui/CategoryTabs';
import CategoryTab from '../../global/Mui/CategoryTab';
import RewardsSearchV2 from '../../global/RewardsSearchV2';
import SearchInputButton from '../../global/SearchInputButton/SearchInputButton';
import MarketingSlider from '../../global/MarketingSlider/MarketingSlider';
import { CATEGORY_SEARCH_PARAM, ROUTE_REWARDS_V2_LIST } from '../../config/routes';
import { ARTSAI_PIXEL_ID } from '../../config/config';
import useGetConfiguration from '../../../../hooks/useGetConfiguration';

import './DashboardRewardsV2.scss';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

const DashboardRewardsV2 = () => {
  const {
    isLoading: isLoadingConfiguration,
    configuration,
  } = useGetConfiguration('dashboardSections');

  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);

  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categories, setCategories] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { userData } = useFirebaseAuth();

  const isLoading = isLoadingConfiguration || isLoadingCategories;
  const dashboardSections = configuration?.sections?.filter(
    ({ enabled, startDate, endDate, type }) => {
      const isEnabled = enabled;
      const currentDate = new Date();
      const isStartDateValid = startDate && new Date(startDate) <= currentDate;
      const isEndDateValid = !endDate || new Date(endDate) >= currentDate;
      const isSlider = type === DASHBOARD_V2_SHELF_TYPES.SLIDER;
      const isSliderAndSubscriptionActive = isSlider && userData?.activeSubscription;

      return isEnabled && isStartDateValid && isEndDateValid && !isSliderAndSubscriptionActive;
    },
  ) ?? [];

  const handleCategoryChange = useCallback((event, newValue) => {
    if (event.target) {
      query.set(CATEGORY_SEARCH_PARAM, newValue);
      history.push({
        pathname: ROUTE_REWARDS_V2_LIST,
        search: query.toString(),
      });
    }
  }, [query, history]);

  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);

    const { data: categoriesData, error } = await getCategories({ maxExchangeValue: 0 });

    if (error) {
      setCategories([FIXED_DEALS_CATEGORIES[0]]);
      setIsLoadingCategories(false);
      return;
    }

    setCategories([FIXED_DEALS_CATEGORIES[0], ...(categoriesData?.categories ?? [])]);
    setIsLoadingCategories(false);
  }, []);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    console.log('Shipment trackers: fetching... (from /deals page)');
    apiFetch({ path: SHIPMENTS_TRACKERS_PATH })
      .then((result) => {
        console.log('Shipment trackers (QA):', result);
      })
      .catch((err) => {
        console.log('Shipment trackers request failed:', err);
      });
  }, []);

  const renderRewardsShelf = useCallback(({ title, type, categoryId }) => {
    if (type !== REWARDS_V2_SHELF_TYPES.CATEGORY) return null;

    const category = categories.find(({ uid }) => uid === categoryId);
    if (!category) return null;

    return (
      <RewardsStoresShelf
        title={title}
        category={category}
        showOnEmptyResults
      />
    );
  }, [categories]);

  const renderMarketingBanner = useCallback(({ title, description, ctaLabel }) => (
    <MarketingBannerV2
      title={title}
      description={description}
      ctaLabel={ctaLabel}
      ctaURL={BROWSER_EXTENSION_URL}
      backgroundImage={LaptopPromosImage}
    />
  ), []);

  const renderSection = useCallback((section) => {
    const { type } = section;

    if (Object.values(REWARDS_V2_SHELF_TYPES).includes(type)) {
      return renderRewardsShelf(section);
    }

    switch (type) {
      case DASHBOARD_V2_SHELF_TYPES.MARKETING_BANNER:
        return (
          <MarketingBannerV2
            title={section.title}
            description={section.description}
            ctaLabel={section.ctaLabel}
            ctaURL={BROWSER_EXTENSION_URL}
            backgroundImage={LaptopPromosImage}
          />
        );
      case DASHBOARD_V2_SHELF_TYPES.SLIDER:
        return <MarketingSlider isDeals />;
      case DASHBOARD_V2_SHELF_TYPES.OFFERS_PREVIEW:
        return <OffersPreviewV2 />;
      default:
        return null;
    }
  }, [renderRewardsShelf, renderMarketingBanner]);

  return (
    <Dashboard>
      {/* Conversion Pixel for [content]- DO NOT MODIFY */}
      <img src={`https://arttrk.com/pixel/?ad_log=referer&action=content&pixid=${ARTSAI_PIXEL_ID}`} width="1" height="1" border="0" alt="Conversion Pixel for content" />
      <div className="DashboardRewardsV2__input-container">
        <SearchInputButton readOnly onFocus={() => setIsSearching(true)} />
      </div>
      {isSearching && (
        <RewardsSearchV2
          onClose={() => setIsSearching(false)}
        />
      )}
      <CategoryTabs
        className="DashboardRewardsV2__tabs"
        onChange={handleCategoryChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="deals categories"
      >
        {categories.map(({ uid, name, iconUrl, routeName, meta }) => (
          <CategoryTab key={uid} value={routeName} label={name} iconBackground={meta?.bgColor} icon={<img src={iconUrl} alt="tab icon" />} />
        ))}
      </CategoryTabs>
      {!isLoading && dashboardSections.map((section) => renderSection(section))}
    </Dashboard>
  );
};

export default DashboardRewardsV2;
